import API from '@aws-amplify/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationCircle, faChevronCircleDown, faChevronCircleUp } from '@fortawesome/free-solid-svg-icons';
import i18n from '../../i18n';
import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ApplicationSection from './application/ApplicationSection';
import Button from '../shared/Button';
import { defaultLanding, questionTypes, userTypes } from '../../utils/enums';
import FileDrop from '../shared/FileDrop';
import DateTimeText from '../shared/DateTimeText';
import Panel from '../shared/Panel';
import StepProgressIndicator from './application/StepProgressIndicator';
import { ProtectedRouteContext } from '../Context';
import MuiFooter from '../shared/muiControls/muiFooter';
import RenderIf from '../renderIf';

import './Application.scss';

export default function Application(props) {
    const [t] = useTranslation();
    const { userSettings } = useContext(ProtectedRouteContext);
    const [lang, setLang] = useState(
        typeof userSettings?.LanguagePreference === 'string' ? userSettings.LanguagePreference : i18n.language
    );
    const appInstanceId = props.match.params.applicationId;
    const [gettingApplicationDefinition, setGettingApplicationDefinition] = useState(true);
    const [applicationDefinition, setApplicationDefinition] = useState({});
    const [shouldShowErrors, setShouldShowErrors] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [spiRender, setSpiRender] = useState(0);
    const [showSubmitted, setShowSubmitted] = useState(false);
    const [viewOnly, setViewOnly] = useState(false);
    const [message, setMessage] = useState();
    const [messageExpanded, setMessageExpanded] = useState(true);
    const history = useHistory();

    /**Used to check and mark the validity of pre-chosen answers and sections.
     * Also applies a validator function to the question object for later use.
     * @param {any} application Application definition with potentially pre-chosen answers.
     */
    const checkQuestionValidity = useCallback((application) => {
        const questionValidatorSingleLinePlainText = (question) => {
            if (question.minResponseRequired > 0 && question.answerText.length === 0)
                return { isValid: false, invalidReason: 'Application.Required' };

            const regexes = question.responseRegexes || [];
            if (regexes.length < 1) return { isValid: true, invalidReason: '' };

            for (let i = 0; i < regexes.length; i++) {
                const validator = regexes[i];
                try {
                    var rgx = new RegExp(validator.rgx);
                } catch (_rgxE) {
                    console.error(`Question ${question.id} has invalid regex in position ${i}.`);
                    continue;
                }
                if (rgx.test(question.answerText)) continue;

                return { isValid: false, invalidReason: validator.errorText };
            }

            return { isValid: true, invalidReason: '' };
        };

        const questionValidatorDropdown = (question) => {
            const chosenVal = question.chosenOptions && question.chosenOptions.length > 0 ? question.chosenOptions[0] : undefined;
            if (!question.minResponseRequired) return { isValid: true, invalidReason: '' };
            if (!!chosenVal) return { isValid: true, invalidReason: '' };
            return { isValid: false, invalidReason: 'Application.Required' };
        };

        const questionValidatorRadio = (question) => {
            const chosenVal = question.chosenOptions && question.chosenOptions.length > 0 ? question.chosenOptions[0] : undefined;
            if (!question.minResponseRequired) return { isValid: true, invalidReason: '' };
            if (!!chosenVal) return { isValid: true, invalidReason: '' };
            return { isValid: false, invalidReason: 'Application.Required' };
        };

        const questionValidatorCheckbox = (question) => {
            const chosenVals = question.chosenOptions.filter((a) => {
                return !!a;
            });
            if (question.maxResponseRequired > 0 && chosenVals.length > question.maxResponseRequired)
                return { isValid: false, invalidReason: 'Application.CheckboxTooMany' };
            if (!question.minResponseRequired) return { isValid: true, invalidReason: '' };
            if (chosenVals.length < question.minResponseRequired) return { isValid: false, invalidReason: 'Application.CheckboxTooFew' };
            return { isValid: true, invalidReason: '' };
        };

        //TODO a date is required if question.minResponseRequired > 0
        const questionValidatorDatePicker = (_question) => {
            var comparisonDate = new Date(_question.answerText);
            var today = new Date();
            if (_question.minResponseRequired > 0 && _question.answerText === '') {
                return { isValid: false, invalidReason: 'Application.MissingDOB' };
            }
            else if(comparisonDate.getFullYear() < 1900 || comparisonDate >= today) {
                return { isValid: false, invalidReason: 'Application.InvalidYear' };
            }
            return { isValid: true, invalidReason: '' };
        };

        const questionValidatorSignaturePad = (question) => {
            const sigImageFileId = question.chosenOptions && question.chosenOptions.length > 0 ? question.chosenOptions[0] : undefined;
            if (!question.minResponseRequired) return { isValid: true, invalidReason: '' };
            if (!!sigImageFileId) return { isValid: true, invalidReason: '' };
            return { isValid: false, invalidReason: 'Application.Required' };
        };

        const questionValidatorOutputPreview = (_question) => {
            return { isValid: true, invalidReason: '' };
        };

        const questionValidatorFileUpload = (question) => {
            const fileIds = question.chosenOptions.filter((a) => {
                return !!a;
            });
            if (question.maxResponseRequired > 0 && fileIds.length > question.maxResponseRequired)
                return { isValid: false, invalidReason: 'Application.FilesTooMany' };
            if (!question.minResponseRequired) return { isValid: true, invalidReason: '' };
            if (fileIds.length < question.minResponseRequired) return { isValid: false, invalidReason: 'Application.FilesTooFew' };
            return { isValid: true, invalidReason: '' };
        };

        const questionValidatorLabelOnly = (_question) => {
            return { isValid: true, invalidReason: '' };
        };

        const questionValidatorTable = (_question) => {
            return { isValid: true, invalidReason: '' };
        };

        const questionValidatorStrikethrough = (_question) => {
            return { isValid: true, invalidReason: '' };
        };

        //#region MultiChoice Validator
        const questionValidatorMultiChoice = (question) => {
            let { chosenOptions, minResponseRequired, maxResponseRequired } = question;

            let validityObj = { isValid: true, invalidReason: '' };

            if (minResponseRequired === 0 || minResponseRequired === undefined) {
                validityObj = { isValid: true, invalidReason: '' };
                return validityObj;
            }

            if (minResponseRequired > 0 && chosenOptions.length < minResponseRequired) {
                validityObj = { isValid: false, invalidReason: `(At least ${minResponseRequired} options are required)` };
                return validityObj;
            }

            if (maxResponseRequired > 0 && chosenOptions.length > maxResponseRequired) {
                validityObj = { isValid: false, invalidReason: `(Please choose no more than ${maxResponseRequired} options)` };
                return validityObj;
            }

            return validityObj;
        };
        //#endregion MultiChoice Validator

        const questionValidatorUnknown = (_question) => {
            return { isValid: false, invalidReason: 'Unknown question type' };
        };
        /**Get a function which can be used validate this question.
         * @param {number} questionType Question type.
         * @returns {(question: ShipyardQuestion) => {
         * isValid: boolean;
         * invalidReason: string;
         * }} Function which can be used to check the validity of the question.
         */
        const getQuestionValidator = (questionType) => {
            switch (questionType) {
                case questionTypes.SingleLinePlainText:
                case questionTypes.MultiLinePlainText:
                    return questionValidatorSingleLinePlainText;
                case questionTypes.Dropdown:
                    return questionValidatorDropdown;
                case questionTypes.DropdownMultichoice:
                    return questionValidatorMultiChoice;
                case questionTypes.Radio:
                    return questionValidatorRadio;
                case questionTypes.Checkbox:
                    return questionValidatorCheckbox;
                case questionTypes.DatePicker:
                    return questionValidatorDatePicker;
                case questionTypes.FileUpload:
                    return questionValidatorFileUpload;
                case questionTypes.SignaturePad:
                    return questionValidatorSignaturePad;
                case questionTypes.LabelOnly:
                    return questionValidatorLabelOnly;
                case questionTypes.Table:
                    return questionValidatorTable;
                case questionTypes.OutputPreview:
                    return questionValidatorOutputPreview;
                case questionTypes.Strikethrough:
                    return questionValidatorStrikethrough;
                default:
                    console.error('Unknown question type encountered:', questionType);
                    return questionValidatorUnknown;
            }
        };

        for (let sectionI = 0; sectionI < application.sections.length; sectionI++) {
            const section = application.sections[sectionI];
            for (let questionI = 0; questionI < section.questions.length; questionI++) {
                const question = section.questions[questionI];
                question.validator = getQuestionValidator(question.questionType);
                var validity = question.validator(question);
                question.isValid = validity.isValid;
                question.invalidReason = validity.invalidReason;
            }
        }
    }, []);

    const answerUpdated = useCallback(() => {
        if (Object.keys(applicationDefinition).length < 1) return;

        for (let sectionI = 0; sectionI < applicationDefinition.sections.length; sectionI++) {
            const section = applicationDefinition.sections[sectionI];
            var questionValidity = section.questions.map((q) => {
                return q.isValid;
            });
            var sectionValid =
                questionValidity.filter((q) => {
                    return !q;
                }).length === 0;
            section.isValid = sectionValid;
        }
        setSpiRender(Math.random());
    }, [applicationDefinition]);

    useEffect(() => {
        // Gets called after setApplicationDefinition completed
        // Calculate question and section validity
        answerUpdated();
        setShouldShowErrors(false);
    }, [applicationDefinition, answerUpdated]);

    useEffect(() => {
        const userCanEdit = (appInstStatus, appStartedBy) => {
            // TODO phase 2 - DB-driven permission matrix
            switch (appInstStatus) {
                case 1: // Started
                    return appStartedBy === userSettings.UserId;
                case 2: // Review
                    return userSettings.UserType === userTypes.PrivateSchool;
                case 3: // Returned
                    return appStartedBy === userSettings.UserId;
                default:
                    return false;
            }
        };

        (async () => {
            if (gettingApplicationDefinition) {
                try {
                    var response = await API.get('ShipyardAPI', '/application/' + appInstanceId);
                } catch (apiGetErr) {
                    //TODO error toast
                    console.error('API error', apiGetErr);
                    history.push(defaultLanding(userSettings.UserType));
                    return;
                }

                //TODO #TechnicalDebt: Refactor this as back-end logic around UserType.
                if (typeof response?.appDef?.sections[0]?.questions?.filter === 'function') {
                    response.appDef.sections[0].questions = response.appDef.sections[0].questions.filter((q) => q.id !== 32);
                }

                if (!!response?.appInstance?.message) setMessage(response.appInstance.message);

                checkQuestionValidity(response.appDef);
                setApplicationDefinition(response.appDef);
                if (!userCanEdit(response.appInstance.statusId, response.appInstance.createdBy)) {
                    setViewOnly(true);
                }
                setCurrentStep(0);
                setShouldShowErrors(false);
                setGettingApplicationDefinition(false);
            }
        })();
    }, [gettingApplicationDefinition, appInstanceId, checkQuestionValidity, history, userSettings.UserType, userSettings.UserId]);

    useEffect(() => {
        if (typeof userSettings?.LanguagePreference === 'string') {
            setLang(userSettings.LanguagePreference);
        }
    }, [userSettings.LanguagePreference]);

    const messageJSX = useMemo(() => {
        return !!message ? (
            <div
                key={`app-${message.AppInstID}`}
                className="review-message"
            >
                <div>
                    <FontAwesomeIcon icon={faExclamationCircle} /> {t(message.ChangeTitle)}{' '}
                    <FontAwesomeIcon
                        className="collapse-button"
                        icon={messageExpanded ? faChevronCircleDown : faChevronCircleUp}
                        onClick={() => setMessageExpanded((prev) => !prev)}
                    />
                </div>
                {messageExpanded ? (
                    <>
                        <hr />
                        <div className="change-reason-container">
                            <div className="message-container">{message.ChangeReason || 'No status change reason provided.'}</div>
                            <div className="signature-container">
                                {message.LastUpdatedByName}
                                <DateTimeText
                                    type="long-time"
                                    date={message.LastUpdatedUTC}
                                />
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        ) : null;
    }, [message, messageExpanded, t]);

    const nextStep = () => {
        //TODO error checking and validation
        if (currentStep < applicationDefinition.sections.length - 1) setCurrentStep(currentStep + 1);
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const prevStep = () => {
        //TODO error checking and validation
        if (currentStep > 0) setCurrentStep(currentStep - 1);
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const submit = async () => {
        if (!applicationDefinition.sections.every((section) => section.isValid === true)) {
            toast.error(applicationDefinition.submissionErrorMarkdown?.[lang] || t('Application.ValidationFailure'));
            setShouldShowErrors(true);
            setSpiRender(Math.random());
            return;
        }

        setSubmitting(true);

        try {
            var submitResponse = await API.patch('ShipyardAPI', `/application/${appInstanceId}`, {
                body: {
                    status: 2,
                },
            });
        } catch (apiError) {
            console.error(apiError);
            toast.error(t('General.AjaxError'));
            return;
        }

        if (!submitResponse.success) {
            toast.error(t('General.AjaxError'));
            return;
        }

        applicationDefinition.generatedFiles = submitResponse.generatedFiles;

        setSubmitting(false);
        setShowSubmitted(true);
    };

    const backToApps = () => {
        history.push('/applications');
    };

    const applicationPlaceholder = (
        <div
            id="application-component"
            className="placeholder"
        >
            <h2>
                <div className="application-name shimmer" />
            </h2>
            <Panel>
                <div className="progress-indicator shimmer" />
                <div className="step-name shimmer" />
                <div className="step-instructions shimmer" />
                <div className="questions">
                    {[...Array(20)].map((_e, i) => (
                        <div
                            className="question"
                            key={i}
                        >
                            <div className="question-text shimmer" />
                            <div className="question-response shimmer" />
                        </div>
                    ))}
                </div>
            </Panel>
        </div>
    );

    return gettingApplicationDefinition ? (
        applicationPlaceholder
    ) : (
        <div id="application-component">
            <h2>
                <ReactMarkdown>{applicationDefinition.name[lang]}</ReactMarkdown>
            </h2>
            {!showSubmitted && !!messageJSX ? messageJSX : null}
            {showSubmitted ? (
                <Panel classes={['submitted']}>
                    <ReactMarkdown children={applicationDefinition.submittedHeaderMarkdown[lang]} />
                    <div className="generated-files-list">
                        {applicationDefinition.generatedFiles.map((generatedFile) => {
                            let fileDate = new Date();
                            let files = [
                                {
                                    name: generatedFile.fileName,
                                    fileId: generatedFile.fileId,
                                    uploadedOn: fileDate,
                                    type: 'pdf',
                                },
                            ];
                            return (
                                <FileDrop
                                    key={`generated-file${generatedFile.fileId}`}
                                    files={files}
                                />
                            );
                        })}
                    </div>
                    <ReactMarkdown children={applicationDefinition.submittedFooterMarkdown[lang]} />
                </Panel>
            ) : (
                <Panel classes={applicationDefinition?.sections?.length <= 1 ? ['no-padding'] : null}>
                    <RenderIf isTrue={applicationDefinition?.sections?.length > 1}>
                        <StepProgressIndicator
                            appDefinition={applicationDefinition}
                            currentStep={currentStep}
                            reRender={spiRender}
                            setStep={setCurrentStep}
                            disabled={submitting}
                        />
                    </RenderIf>
                    <ApplicationSection
                        appInstanceId={appInstanceId}
                        section={applicationDefinition.sections[currentStep]}
                        answerUpdated={answerUpdated}
                        shouldShowErrors={shouldShowErrors}
                        disabled={submitting | viewOnly}
                    />
                </Panel>
            )}
            <MuiFooter
                rightContent={[
                    <RenderIf
                        key="back-to-apps"
                        isTrue={showSubmitted}
                    >
                        <Button
                            className="back-to-apps"
                            interactive={false}
                            onClick={backToApps}
                        >
                            {t('Application.BackToApplications')}
                        </Button>
                    </RenderIf>,
                    <RenderIf
                        key="prev-step"
                        isTrue={!showSubmitted && currentStep > 0}
                    >
                        <Button
                            type="grey"
                            disabled={submitting}
                            onClick={prevStep}
                        >
                            {`Back to ${applicationDefinition.sections[currentStep - 1]?.name[lang]}`}
                        </Button>
                    </RenderIf>,
                    <RenderIf
                        key="submit-button"
                        isTrue={!showSubmitted && currentStep + 1 === applicationDefinition.sections.length}
                    >
                        <Button
                            className="submit"
                            interactive={false}
                            onClick={submit}
                            disabled={submitting || viewOnly}
                        >
                            {submitting ? (
                                <div>
                                    <FontAwesomeIcon icon={faSpinner} />
                                    <span>{t('Application.Submitting')}</span>
                                </div>
                            ) : (
                                <div>
                                    <span>{t('Application.Submit')}</span>
                                </div>
                            )}
                        </Button>
                    </RenderIf>,
                    <RenderIf
                        key="continue-button"
                        isTrue={!showSubmitted && !(currentStep + 1 === applicationDefinition.sections.length)}
                    >
                        <Button
                            interactive={false}
                            onClick={nextStep}
                        >
                            {t('General.Continue')}
                        </Button>
                    </RenderIf>,
                ]}
                paperSx={{
                    padding: '1rem',
                }}
            />
        </div>
    );
}

